import {
  DataWorkflowStatus,
  ExportStatus,
  ReportStatus,
} from "services/adi/types";
import { StatusIndicatorProps } from "@amzn/awsui-components-react";

export interface DataWorkflowRow {
  principalId: string;
  datasetName: string;
  datasetType?: string;
  status: DataWorkflowStatus;
  recordCount: number | null;
  countryCode: string;
  entityCode?: string;
  taxTypes?: string;
  dateRangeLabel?: string;
  gaiaContextId?: string;
  taskName?: string;
  createdBy: string;
  createdAt: string;
}

export type DataInsightsListProps = Record<string, never>;

export const STATUS_INDICATOR_MAP: Record<
  DataWorkflowStatus,
  StatusIndicatorProps.Type
> = {
  [DataWorkflowStatus.SUBMITTED]: "pending",
  [DataWorkflowStatus.RUNNING]: "in-progress",
  [DataWorkflowStatus.AVAILABLE]: "success",
  [DataWorkflowStatus.FAILED]: "error",
  [DataWorkflowStatus.CANCELED]: "stopped",
};

export const STATUS_LABEL_MAP: Record<DataWorkflowStatus, string> = {
  [DataWorkflowStatus.SUBMITTED]: "Submitted",
  [DataWorkflowStatus.RUNNING]: "In progress",
  [DataWorkflowStatus.AVAILABLE]: "Complete",
  [DataWorkflowStatus.FAILED]: "Failed",
  [DataWorkflowStatus.CANCELED]: "Canceled",
};

export const REPORT_STATUS_INDICATOR_MAP: Record<
  ReportStatus,
  StatusIndicatorProps.Type
> = {
  [ReportStatus.SUBMITTED]: "pending",
  [ReportStatus.RUNNING]: "in-progress",
  [ReportStatus.AVAILABLE]: "success",
  [ReportStatus.FAILED]: "error",
  [ReportStatus.CANCELED]: "stopped",
};

export const REPORT_STATUS_LABEL_MAP: Record<ReportStatus, string> = {
  [ReportStatus.SUBMITTED]: "Pending",
  [ReportStatus.RUNNING]: "Running",
  [ReportStatus.AVAILABLE]: "Complete",
  [ReportStatus.FAILED]: "Failed",
  [ReportStatus.CANCELED]: "Canceled",
};

export const EXPORT_STATUS_INDICATOR_MAP: Record<
  ExportStatus,
  StatusIndicatorProps.Type
> = {
  [ExportStatus.PENDING]: "pending",
  [ExportStatus.RUNNING]: "in-progress",
  [ExportStatus.COMPLETED]: "success",
  [ExportStatus.FAILED]: "error",
};

export const EXPORT_STATUS_LABEL_MAP: Record<ExportStatus, string> = {
  [ExportStatus.PENDING]: "Pending",
  [ExportStatus.RUNNING]: "Running",
  [ExportStatus.COMPLETED]: "Complete",
  [ExportStatus.FAILED]: "Failed",
};

/** TemplateName is @length(1, 255) in the model. */
export const MAX_TEMPLATE_NAME_LENGTH = 255;

/** TemplateSql is @length(1, 10000) in the model. */
export const MAX_TEMPLATE_SQL_LENGTH = 10_000;

/** TeamId is @length(1, 64) in the model. */
export const MAX_TEAM_ID_LENGTH = 64;

export const TEMPLATE_NAME_REQUIRED = "Template name is required.";
export const TEMPLATE_NAME_TOO_LONG = `Template name must be ${MAX_TEMPLATE_NAME_LENGTH} characters or fewer.`;
export const TEMPLATE_COLUMNS_REQUIRED =
  "Select at least one column before creating a template.";
export const TEMPLATE_QUERY_REQUIRED =
  "Write a query before creating a template.";
export const TEMPLATE_QUERY_TOO_LONG = `Query must be ${MAX_TEMPLATE_SQL_LENGTH} characters or fewer.`;
export const TEMPLATE_TEAM_REQUIRED =
  "No team is selected. Choose a team before creating a template.";
export const TEMPLATE_TEAM_INVALID =
  "The selected team cannot be used for templates.";
export const TEMPLATE_COLUMN_FUNCTION_REQUIRED =
  "Every column needs an aggregation function.";
export const TEMPLATE_NON_TIME_COLUMN_REQUIRED =
  "Add at least one column besides the time column.";
export const TEMPLATE_MEASURE_REQUIRED =
  "At least one column must have an aggregation measure (SUM, COUNT, AVG, MIN, or MAX).";
export const TEMPLATE_TIME_COLUMN_REQUIRED =
  "Choose a time column before creating a template.";
export const TEMPLATE_TIME_GRANULARITY_REQUIRED =
  "Choose a time granularity before creating a template.";
export const TEMPLATE_DATE_PERIOD_REQUIRED =
  "Choose a date period for the report output.";
export const TEMPLATE_DATE_FIELD_REQUIRED =
  "Choose a date field for the report output.";
